// components/UserDetail.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { baseURL } from '../config';
import { FaEdit, FaArrowLeft, FaHistory, FaRupeeSign, FaPhone, FaUser, FaCloudUploadAlt, FaKey } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FinancialReport from '../components/reusable/financialDashboard';
import DateRangeWithLine from '../components/reusable/DateSelector1';
import UserList from '../components/reusable/usersList';
import PageHeader from '../components/reusable/PageHeader';

function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [userId, setUserId] = useState();
  const [username, setUserName] = useState();
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const fileInputRef = useRef(null);
  const location = useLocation();
  const passedUser = location.state;

  useEffect(() => {
    if (passedUser) {
      setUserName(passedUser.username);
      setFormData(passedUser);
      setImagePreview(passedUser.profile_img);
      setLoading(false);
      setUser(passedUser);
    }
  }, [passedUser]);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    axios.post(`${baseURL}/singleUser`, { userId })
      .then(({ data }) => {
        const user = data[0] || {};
        setUser(user);
        setFormData(user);
        setImagePreview(user.profile_img);
        setUserName(user.username);
      })
      .catch((err) => {
        console.error("Failed to fetch user:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userId]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setProfileImageFile(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const validatePhone = (phone) => {
    return /^\d{10}$/.test(phone);
  };

  const validatePassword = (password) => {
    return /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(password);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    
    if (formData.phone && !validatePhone(formData.phone)) {
      toast.error("Phone number must be 10 digits");
      return;
    }
    
    if (formData.password && !validatePassword(formData.password)) {
      toast.error("Password must be at least 8 characters with uppercase, number, and special character");
      return;
    }

    try {
      const form = new FormData();
      form.append("name", formData.name);
      form.append("mobileNumber", formData.mobile_number);
      
      if (formData.password) {
        form.append("password", formData.password);
      }
      
      if (profileImageFile) {
        form.append("image", profileImageFile);
      }

      const response = await axios.put(
        `${baseURL}/editUser/${id}`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setUser(response.data.user);
      setEditMode(false);
      setProfileImageFile(null);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error("Failed to update profile. Please try again.");
    }
  };

  const cancelEdit = () => {
    setEditMode(false);
    setProfileImageFile(null);
    setImagePreview(user.profile_img);
  };

  // Reset Password Functions
  const openResetModal = () => {
    setIsResetModalOpen(true);
  };

  const closeResetModal = () => {
    setIsResetModalOpen(false);
    setResetPasswordData({
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleResetPasswordChange = (e) => {
    const { name, value } = e.target;
    setResetPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleResetSubmit = async () => {
    if (!resetPasswordData.newPassword || !resetPasswordData.confirmPassword) {
      toast.error("Both password fields are required");
      return;
    }

    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (!validatePassword(resetPasswordData.newPassword)) {
      toast.error("Password must be at least 8 characters with uppercase, number, and special character");
      return;
    }

    try {
      const response = await axios.post(
        `${baseURL}/user/resetpassword`,
        {
          userId: user.id,
          username: user.username,
          newPassword: resetPasswordData.newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );
      
      if (response.data.success) {
        toast.success("Password reset successfully!");
        closeResetModal();
      } else {
        toast.error("Failed to reset password. Please try again.");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error("An error occurred while resetting password");
    }
  };

  if (!user) return <div className="text-center py-10">User not found</div>;
  
  const handleDateRangeSelection = (startDate, endDate) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };
  
  const handleUserSelect = (selectedUserId) => {
    setUserId(selectedUserId);
  };

  return (
    <div className="w-full mx-auto p-4 overflow-y-scroll">
      <PageHeader title="Back to customers"/>
      
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-md mb-6 md:mb-8 w-full">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 items-center">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select User
            </label>
            <div className="relative">
              <UserList onSelectUser={handleUserSelect} selectedUser={username}/>
            </div>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Range
            </label>
            <div className="relative">
              <DateRangeWithLine handleChange={handleDateRangeSelection} />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6 text-white">
          <div className="flex flex-col md:flex-row items-center">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt={user.name}
                className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover"
              />
            ) : (
              <div className="bg-gray-200 border-4 border-white rounded-full w-24 h-24 flex items-center justify-center shadow-lg">
                <FaUser className="text-gray-400 text-4xl" />
              </div>
            )}

            <div className="mt-4 md:mt-0 md:ml-6 text-center md:text-left">
              <h1 className="text-3xl font-bold">{user.name}</h1>
              <p className="mt-2 flex items-center justify-center md:justify-start">
                <FaPhone className="mr-2" /> {user.mobile_number || "N/A"}
              </p>
              <div className="mt-4 flex flex-wrap gap-3 justify-center md:justify-start">
                <button
                  onClick={() => navigate(`/view-entries`, { state: user })}
                  className="flex items-center bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                >
                  <FaHistory className="mr-2" /> View Milk Entries
                </button>
                <button
                  onClick={() => navigate(`/balance`, { state: user })}
                  className="flex items-center bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                >
                  <FaRupeeSign className="mr-2" /> View Balance
                </button>
                <button
                  onClick={() => setEditMode(!editMode)}
                  className="flex items-center bg-blue-700 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-800 transition-colors"
                >
                  <FaEdit className="mr-2" /> Edit Profile
                </button>
                <button
                  onClick={openResetModal}
                  className="flex items-center bg-blue-700 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-800 transition-colors"
                >
                  <FaKey className="mr-2" /> Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>

        {editMode && (
          <div className="p-6 border-b">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-gray-700 mb-2">
                    Profile Image
                  </label>
                  <div className="flex items-center">
                    <div className="relative">
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-24 h-24 rounded-full border-2 border-dashed border-gray-300 object-cover"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                          <FaUser className="text-gray-400 text-2xl" />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={triggerFileInput}
                        className="absolute bottom-0 right-0 bg-blue-500 text-white rounded-full p-2 shadow-md hover:bg-blue-600 transition-colors"
                      >
                        <FaCloudUploadAlt />
                      </button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        accept="image/*"
                        className="hidden"
                      />
                    </div>
                    <div className="ml-4 text-sm text-gray-500">
                      <p>Click the upload button to change profile image</p>
                      <p className="mt-1">Max file size: 5MB</p>
                      {profileImageFile && (
                        <p className="mt-1 text-green-600">
                          {profileImageFile.name} selected
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 mb-2">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name || ""}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile_number || ""}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    pattern="[0-9]{10}"
                    required
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="p-6">
          <FinancialReport
            startDate={startDate}
            endDate={endDate}
            userId={userId}
            userName={user.name}
            userMobile={user.username}
          />
        </div>
      </div>

      {/* Reset Password Modal */}
      {isResetModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">Reset Password</h2>
              <p className="mb-4">
                Resetting password for: <span className="font-semibold">{user.name}</span>
              </p>
              
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">New Password</label>
                <input
                  type="password"
                  name="newPassword"
                  value={resetPasswordData.newPassword}
                  onChange={handleResetPasswordChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter new password"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 mb-2">Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={resetPasswordData.confirmPassword}
                  onChange={handleResetPasswordChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Confirm new password"
                />
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={closeResetModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDetail;